import { useForm } from '@tanstack/react-form'
import {
  useAddCategoryMutation,
  useUpdateCategoryMutation,
} from '../hooks/index'
import { useExpensesStore } from '../store/expensesStore'
import {
  ExpenseCategoryCreateSchema,
  ExpenseCategoryUpdateSchema,
} from '../schema/expensesSchema'
import type { ApiError } from '@/types/api'
import { FormInput } from '@/components/custom/form-input'
import { FormTextarea } from '@/components/custom/form-textarea'
import LoadingButton from '@/components/custom/loading-button'
import { Modal } from '@/components/custom/modal'
import { notifyRequestError } from '@/utils/notifyRequestError'
import { Switch } from '@/components/custom/switch'

interface CategoryFormProps {
  id?: string | null
  type: 'add' | 'edit'
  trigger?: React.ReactNode
  open?: boolean
  onClose?: () => void
}

export function ExpenseCategoryForm({
  type,
  trigger,
  open,
  onClose,
  id: initialId,
}: CategoryFormProps) {
  const { mutateAsync: addCategoryMutation, isPending: isAdding } =
    useAddCategoryMutation(() => handleClose())
  const { mutateAsync: updateCategoryMutation, isPending: isUpdating } =
    useUpdateCategoryMutation(() => handleClose())

  const categories = useExpensesStore((state) => state.categories)
  const initial = categories.find((category) => category._id === initialId)

  const form = useForm({
    defaultValues:
      type === 'edit'
        ? {
            _id: initial?._id || '',
            name: initial?.name || '',
            description: initial?.description || '',
            active: initial?.active ?? false,
          }
        : {
            name: '',
            description: '',
          },
    onSubmit: async ({ value }) => {
      try {
        if (type === 'edit' && initial) {
          await updateCategoryMutation({
            ...value,
            _id: initial._id,
          } as any)
        } else {
          await addCategoryMutation(value)
        }
      } catch (error) {
        notifyRequestError(
          error as ApiError,
          'Error occurred while submitting category form',
        )
      }
    },
  })

  const handleClose = () => {
    onClose?.()
    form.reset()
  }

  const isPending = isAdding || isUpdating
  const buttonLabel = type === 'add' ? 'Add' : 'Update'
  const loadingLabel = type === 'add' ? 'Adding...' : 'Updating...'

  const schema =
    type === 'edit' ? ExpenseCategoryUpdateSchema : ExpenseCategoryCreateSchema

  return (
    <Modal
      open={open}
      onClose={handleClose}
      trigger={trigger}
      title={type === 'edit' ? 'Edit Category' : 'Add Category'}
      direction="right"
      contentClassName="min-w-[98vw]"
    >
      <form
        id="category-form"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
        className="space-y-2 h-full flex flex-col"
      >
        <div className="flex-1 overflow-y-auto space-y-2">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Category Name */}
            <form.Field
              name="name"
              validators={{
                onBlur: ({ value }) => {
                  const result = schema.shape.name.safeParse(value)
                  return result.success
                    ? undefined
                    : result.error.errors[0].message
                },
              }}
              children={(field) => {
                return (
                  <div className="gap-0 flex-1">
                    <FormInput
                      field={field}
                      label="Category Name"
                      type="text"
                      id={field.name}
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      disabled={isPending}
                      required
                    />
                  </div>
                )
              }}
            />

            {type === 'edit' && (
              <form.Field
                name="active"
                validators={{
                  onBlur: ({ value }) => {
                    const result = schema.safeParse({
                      ...form.state.values,
                      active: value,
                    })
                    return result.success
                      ? undefined
                      : result.error.errors.find((e) => e.path[0] === 'active')
                          ?.message
                  },
                }}
                children={(field) => {
                  return (
                    <div className="gap-0">
                      <Switch
                        field={field}
                        label="Active"
                        id={field.name}
                        name={field.name}
                      />
                    </div>
                  )
                }}
              />
            )}
          </div>

          <form.Field
            name="description"
            children={(field) => (
              <FormTextarea
                field={field}
                label="Description"
                id={field.name}
                name={field.name}
                placeholder="Describe what this category covers..."
                disabled={isPending}
                rows={3}
              />
            )}
          />
        </div>
        {/* Buttons */}
        <div className="flex gap-2 border-t pt-2 mt-4 justify-end">
          <LoadingButton
            form="category-form"
            isLoading={isPending}
            disabled={isPending}
            type="submit"
          >
            {isPending ? loadingLabel : buttonLabel}
          </LoadingButton>
        </div>
      </form>
    </Modal>
  )
}
